/**
 * app-origin — CANONICAL trusted application origin for auth email destinations.
 *
 * PROVEN DEFECT THIS MODULE FIXES
 * -------------------------------
 * Auth email redirects were derived from `window.location.origin`. When the
 * public funnel was driven from a dev/preview host, GoTrue received
 * `http://localhost:8080/auth/callback?scan_request_id=…` as `redirect_to`.
 * That origin is not allow-listed, so GoTrue silently discarded it and fell
 * back to the Site URL — the emailed link could never reach `/auth/callback`
 * and the scan identity was lost.
 *
 * RULES (system-wide, every tenant, current and future)
 *   • A production auth email destination ALWAYS resolves to
 *     `https://authoritylayer.app`.
 *   • Only explicitly allow-listed origins may be used at all.
 *   • localhost / 127.0.0.1 / unknown hosts are NEVER emitted into an auth
 *     email — they degrade to the production origin.
 *   • `www.` is normalised to the canonical apex origin.
 *   • The caller's path + query (e.g. `scan_request_id`) is preserved byte-exact.
 *
 * PURE module: no React, no Supabase, no side effects.
 */

/** The one canonical production origin for customer-visible auth links. */
export const PRODUCTION_APP_ORIGIN = "https://authoritylayer.app";

/** Hosts that ARE production (all normalise to PRODUCTION_APP_ORIGIN). */
const PRODUCTION_HOSTS = new Set(["authoritylayer.app", "www.authoritylayer.app"]);

/**
 * Non-production origins that may legitimately receive auth emails while
 * developing/QA-ing the flow. Lovable preview hosts only — never localhost,
 * because a localhost link is undeliverable to a real inbox.
 */
function isAllowedPreviewHost(host: string): boolean {
  return host === "lovable.app" || host.endsWith(".lovable.app");
}

/** True when this origin is allowed to appear inside an auth email. */
export function isAllowedAuthEmailOrigin(origin: string | null | undefined): boolean {
  if (!origin) return false;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  return PRODUCTION_HOSTS.has(url.hostname) || isAllowedPreviewHost(url.hostname);
}

/** True when this origin is the canonical production surface. */
export function isProductionOrigin(origin: string | null | undefined): boolean {
  if (!origin) return false;
  try {
    return PRODUCTION_HOSTS.has(new URL(origin).hostname);
  } catch {
    return false;
  }
}

/**
 * Resolve the origin an auth email link must point at.
 *
 * `browserOrigin` is advisory ONLY: it can promote nothing and can never
 * introduce an unlisted host. Production hosts collapse to the canonical apex.
 */
export function resolveAuthEmailOrigin(browserOrigin?: string | null): string {
  if (isProductionOrigin(browserOrigin)) return PRODUCTION_APP_ORIGIN;
  if (isAllowedAuthEmailOrigin(browserOrigin)) {
    try {
      return new URL(browserOrigin!).origin;
    } catch {
      return PRODUCTION_APP_ORIGIN;
    }
  }
  // localhost, http, sandbox, unknown → never emitted into an email.
  return PRODUCTION_APP_ORIGIN;
}

/** The current browser origin, or null outside a browser. */
function currentBrowserOrigin(): string | null {
  if (typeof window === "undefined" || !window.location) return null;
  return window.location.origin ?? null;
}

/**
 * Build a trusted absolute auth-email destination.
 *
 * @param path   app path, must start with "/" (e.g. "/auth/callback")
 * @param params query params to preserve exactly (e.g. { scan_request_id })
 */
export function buildAuthEmailRedirect(
  path: string,
  params?: Record<string, string | null | undefined>,
  browserOrigin?: string | null,
): string {
  const origin = resolveAuthEmailOrigin(
    browserOrigin === undefined ? currentBrowserOrigin() : browserOrigin,
  );
  const safePath = path.startsWith("/") ? path : `/${path}`;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (typeof value === "string" && value.length > 0) qs.set(key, value);
  }
  const query = qs.toString();
  return `${origin}${safePath}${query ? `?${query}` : ""}`;
}

/** Canonical free-scan verification destination for a given scan request. */
export function buildFreeScanCallbackUrl(
  scanRequestId: string,
  browserOrigin?: string | null,
): string {
  return buildAuthEmailRedirect("/auth/callback", { scan_request_id: scanRequestId }, browserOrigin);
}
